export type InAppBrowserApp =
  | 'LinkedIn'
  | 'Facebook'
  | 'Instagram'
  | 'X'
  | 'TikTok'
  | 'Snapchat'
  | 'Line'
  | 'WeChat'
  | 'Slack'
  | 'Discord'
  | 'WhatsApp'
  | 'Generic';

export type InAppBrowserPlatform = 'ios' | 'android' | 'desktop';

export type InAppBrowserInfo = {
  app: InAppBrowserApp;
  platform: InAppBrowserPlatform;
};

export type BrowserTarget =
  | 'safari' // iOS only
  | 'chrome' // iOS + Android
  | 'firefox' // iOS + Android
  | 'default'; // Android only — uses whichever browser the user has set as default

const PATTERNS: Array<{ re: RegExp; app: InAppBrowserApp }> = [
  { re: /LinkedInApp/i, app: 'LinkedIn' },
  { re: /\b(FBAN|FBAV|FB_IAB|FB4A|FBIOS)\b/i, app: 'Facebook' },
  { re: /Instagram/i, app: 'Instagram' },
  { re: /Twitter|TwitterAndroid/i, app: 'X' },
  { re: /TikTok|musical_ly|BytedanceWebview/i, app: 'TikTok' },
  { re: /Snapchat/i, app: 'Snapchat' },
  { re: /\bLine\//i, app: 'Line' },
  { re: /WeChat|MicroMessenger/i, app: 'WeChat' },
  { re: /Slack/i, app: 'Slack' },
  { re: /Discord/i, app: 'Discord' },
  { re: /WhatsApp/i, app: 'WhatsApp' },
  // Generic Android webview marker — keep last so named apps win.
  { re: /; wv\)/i, app: 'Generic' },
];

const detectPlatform = (userAgent: string): InAppBrowserPlatform => {
  if (/iPhone|iPad|iPod/i.test(userAgent)) return 'ios';
  // iPadOS 13+ can advertise a desktop-class Macintosh UA from a mobile WebKit
  // environment; "Mobile" in the UA disambiguates it from real macOS.
  if (/Macintosh/i.test(userAgent) && /Mobile/i.test(userAgent)) return 'ios';
  if (/Android/i.test(userAgent)) return 'android';
  return 'desktop';
};

const getDefaultUserAgent = (): string =>
  typeof navigator === 'undefined' ? '' : navigator.userAgent;

export const detectInAppBrowser = (
  userAgent: string = getDefaultUserAgent(),
): InAppBrowserInfo | null => {
  for (const { re, app } of PATTERNS) {
    if (re.test(userAgent)) {
      return { app, platform: detectPlatform(userAgent) };
    }
  }
  return null;
};

export const isInAppBrowser = (userAgent?: string): boolean =>
  detectInAppBrowser(userAgent) !== null;

const ANDROID_PACKAGES: Partial<Record<BrowserTarget, string>> = {
  chrome: 'com.android.chrome',
  firefox: 'org.mozilla.firefox',
};

/**
 * Drop the fragment (and any userinfo) before constructing a handoff URL.
 * Android intent URLs use `#Intent;…;end` as a field block, so an attacker
 * who can put `#Intent;action=…` in the page URL would otherwise inject
 * arbitrary intent parameters into our handoff. Stripping the fragment also
 * prevents an Auth0 callback fragment (code+state) from leaking to another
 * browser if a button is clicked mid-flow.
 */
const sanitizeHandoffUrl = (targetUrl: string): string | null => {
  try {
    const u = new URL(targetUrl);
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
    return `${u.protocol}//${u.host}${u.pathname}${u.search}`;
  } catch {
    return null;
  }
};

const buildAndroidIntentUrl = (
  target: BrowserTarget,
  safeUrl: string,
): string => {
  const stripped = safeUrl.replace(/^https?:\/\//, '');
  const fallback = encodeURIComponent(safeUrl);
  const pkg = ANDROID_PACKAGES[target];
  const packagePart = pkg ? `package=${pkg};` : '';
  return `intent://${stripped}#Intent;scheme=https;${packagePart}S.browser_fallback_url=${fallback};end`;
};

/**
 * Build a deep-link URL that hands the page off from an in-app webview to
 * the named browser. Returns null if the (platform, target) combination has
 * no reliable handoff scheme, or if the target URL is malformed / not http(s).
 */
export const buildBrowserHandoffUrl = (
  platform: InAppBrowserPlatform,
  target: BrowserTarget,
  targetUrl: string,
): string | null => {
  const safeUrl = sanitizeHandoffUrl(targetUrl);
  if (!safeUrl) return null;
  if (platform === 'ios') {
    switch (target) {
      case 'safari':
        return `x-safari-${safeUrl}`;
      case 'chrome':
        // googlechromes:// forces HTTPS; we never hand off plain http.
        return `googlechromes://${safeUrl.replace(/^https?:\/\//, '')}`;
      case 'firefox':
        return `firefox://open-url?url=${encodeURIComponent(safeUrl)}`;
      default:
        return null;
    }
  }
  if (platform === 'android') {
    if (target === 'safari') return null;
    return buildAndroidIntentUrl(target, safeUrl);
  }
  return null;
};

/**
 * The browsers we offer as a click target, in display order, for the given
 * platform. Empty for desktop.
 */
export const supportedBrowserTargets = (
  platform: InAppBrowserPlatform,
): BrowserTarget[] => {
  if (platform === 'ios') return ['safari', 'chrome', 'firefox'];
  if (platform === 'android') return ['default', 'chrome', 'firefox'];
  return [];
};

export const BROWSER_TARGET_LABEL: Record<BrowserTarget, string> = {
  safari: 'Open in Safari',
  chrome: 'Open in Chrome',
  firefox: 'Open in Firefox',
  default: 'Open in default browser',
};
